import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ArrowUpRight, Send } from "lucide-react";
import { toast } from "sonner";
import { grantAccess } from "@/utils/access";
import { isTelegramApp, confirmPhoneViaTelegram } from "@/utils/telegram";

import { API } from "../utils/api";

const PHONE_RE = /^\+?[0-9\s\-()]{7,20}$/;
const BOT_URL = "https://t.me/afanasievpro_bot";

const validate = (name, phone, inTelegram) => {
  const errors = {};
  if (name.trim().length < 2) {
    errors.name = "Введите имя (минимум 2 символа)";
  }
  if (!inTelegram) {
    const digits = phone.replace(/\D/g, "");
    if (!PHONE_RE.test(phone.trim()) || digits.length < 7 || digits.length > 15) {
      errors.phone = "Введите корректный номер (7–15 цифр)";
    }
  }
  return errors;
};

const formatApiError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Что-то пошло не так. Попробуйте ещё раз.";
};

export const PreorderForm = () => {
  const inTelegram = isTelegramApp();
  const [name, setName] = useState(() => {
    try {
      return window.Telegram?.WebApp?.initDataUnsafe?.user?.first_name || "";
    } catch {
      return "";
    }
  });
  const [phone, setPhone] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate(name, phone, inTelegram);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setLoading(true);
    try {
      if (inTelegram) {
        // Номер присылает сам Telegram нативным диалогом — ввести чужой нельзя
        await confirmPhoneViaTelegram(name.trim());
        setDone(true);
        toast.success("Номер подтверждён · +1 примерка в подарок");
      } else {
        await axios.post(`${API}/leads`, { name: name.trim(), phone: phone.trim() });
        grantAccess(phone.trim());
        setDone(true);
        toast.success("Заявка отправлена");
      }
    } catch (err) {
      if (err?.message === "cancelled") {
        toast.info("Вы закрыли диалог — номер не подтверждён");
      } else if (err?.message === "unsupported") {
        toast.error("Обновите Telegram — в этой версии подтверждение номера недоступно");
      } else {
        toast.error(formatApiError(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setDone(false);
    setPhone("");
    setErrors({});
  };

  return (
    <div
      className="border border-white/10 bg-[#0A0A0A]/60 p-8 backdrop-blur-2xl md:p-12"
      data-testid="preorder-card"
    >
      <AnimatePresence mode="wait">
        {done ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="flex min-h-[320px] flex-col items-start justify-center"
            data-testid="preorder-success-message"
          >
            <span className="mb-8 flex h-14 w-14 items-center justify-center rounded-full border border-[#C2A878]">
              <Check size={22} strokeWidth={1.5} className="text-[#C2A878]" />
            </span>
            <h3 className="font-cormorant text-4xl font-light text-[#F4F4F0]">
              Спасибо{name.trim() ? `, ${name.trim()}` : ""}!
            </h3>
            {inTelegram ? (
              <p className="mt-4 max-w-sm text-base leading-relaxed text-[#A3A3A3]">
                Номер подтверждён, доступ открыт навсегда, а примерка в подарок
                уже на вашем счёте.
              </p>
            ) : (
              <p className="mt-4 max-w-sm text-base leading-relaxed text-[#A3A3A3]">
                Маски и анализ лица открыты навсегда. А бесплатную примерку
                начислим после подтверждения номера — в мини-приложении Telegram
                это один тап.
              </p>
            )}
            {inTelegram ? (
              <Link
                to="/preview"
                className="mt-10 flex items-center gap-3 border border-[#C2A878] px-8 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                data-testid="preorder-open-app-button"
              >
                <ArrowUpRight size={14} strokeWidth={1.5} />
                Открыть приложение
              </Link>
            ) : (
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <a
                  href={BOT_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-3 border border-[#C2A878] px-8 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A]"
                  data-testid="preorder-open-telegram-button"
                >
                  <Send size={14} strokeWidth={1.5} />
                  Открыть в Telegram
                </a>
                <Link
                  to="/preview"
                  className="flex items-center gap-3 px-2 py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#A3A3A3] transition-colors duration-500 hover:text-[#C2A878]"
                  data-testid="preorder-open-app-button"
                >
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                  Смотреть маски
                </Link>
              </div>
            )}
            {!inTelegram && (
              <button
                onClick={reset}
                className="mt-8 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#525252] underline-offset-8 transition-colors duration-500 hover:text-[#C2A878] hover:underline"
                data-testid="preorder-another-button"
              >
                Отправить ещё один контакт
              </button>
            )}
          </motion.div>
        ) : (
          <motion.form
            key="form"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5 }}
            onSubmit={handleSubmit}
            noValidate
            data-testid="preorder-form"
          >
            <div className="space-y-10">
              <div>
                <label
                  htmlFor="preorder-name"
                  className="mb-3 block font-jetbrains text-xs uppercase tracking-[0.25em] text-[#A3A3A3]"
                >
                  Имя
                </label>
                <input
                  id="preorder-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Как к вам обращаться"
                  autoComplete="name"
                  className="w-full border-b-2 border-white/10 bg-transparent py-3 text-lg font-light text-[#F4F4F0] placeholder:text-[#525252] focus:border-[#C2A878] focus:outline-none transition-colors duration-500"
                  data-testid="preorder-name-input"
                />
                {errors.name && (
                  <p className="mt-2 text-sm text-red-400/80" data-testid="preorder-name-error">
                    {errors.name}
                  </p>
                )}
              </div>

              {inTelegram ? (
                <div data-testid="preorder-tg-note">
                  <p className="mb-3 block font-jetbrains text-xs uppercase tracking-[0.25em] text-[#A3A3A3]">
                    Телефон
                  </p>
                  <p className="border border-[#C2A878]/30 bg-[#C2A878]/[0.06] px-5 py-4 text-sm leading-relaxed text-[#C2A878]">
                    Номер вводить не нужно — Telegram подтвердит его сам,
                    одной кнопкой ниже.
                  </p>
                </div>
              ) : (
                <div>
                  <label
                    htmlFor="preorder-phone"
                    className="mb-3 block font-jetbrains text-xs uppercase tracking-[0.25em] text-[#A3A3A3]"
                  >
                    Телефон
                  </label>
                  <input
                    id="preorder-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+7 900 000-00-00"
                    autoComplete="tel"
                    className="w-full border-b-2 border-white/10 bg-transparent py-3 text-lg font-light text-[#F4F4F0] placeholder:text-[#525252] focus:border-[#C2A878] focus:outline-none transition-colors duration-500"
                    data-testid="preorder-phone-input"
                  />
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-400/80" data-testid="preorder-phone-error">
                      {errors.phone}
                    </p>
                  )}
                </div>
              )}
            </div>

            <label className="mt-6 flex cursor-pointer items-start gap-3" data-testid="preorder-consent">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-0.5 accent-[#C2A878]"
                data-testid="preorder-consent-checkbox"
              />
              <span className="text-xs leading-relaxed text-[#525252]">
                Соглашаюсь с{" "}
                <Link to="/consent" className="text-[#A3A3A3] underline underline-offset-2 transition-colors duration-300 hover:text-[#C2A878]">
                  обработкой персональных данных
                </Link>{" "}
                и{" "}
                <Link to="/privacy" className="text-[#A3A3A3] underline underline-offset-2 transition-colors duration-300 hover:text-[#C2A878]">
                  политикой конфиденциальности
                </Link>
              </span>
            </label>
            <button
              type="submit"
              disabled={loading || !consent}
              className="mt-12 flex w-full items-center justify-center gap-3 border border-[#C2A878] py-4 font-jetbrains text-xs uppercase tracking-[0.25em] text-[#C2A878] transition-colors duration-500 hover:bg-[#C2A878] hover:text-[#0A0A0A] disabled:cursor-not-allowed disabled:opacity-50"
              data-testid="preorder-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Отправляем
                </>
              ) : inTelegram ? (
                "Подтвердить номер в Telegram"
              ) : (
                "Получить доступ"
              )}
            </button>

            <p className="mt-6 text-xs leading-relaxed text-[#525252]">
              {inTelegram
                ? "Имя и один тап. Примерка в подарок — и никакого спама."
                : "Всего два поля. Подтвердите номер в Telegram — и получите бесплатную примерку."}
            </p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
